// Supplier CRUD — warehouse+ (gồm role sales) xem/thêm/sửa; ô Công nợ manager+.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, X, Check, BookOpen } from "lucide-react";
import { SupplierLedgerModal } from "@/features/debts/supplier-ledger-modal";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore, roleAllows, type UserRole } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface Supplier {
  id: string;
  name: string;
  phone: string;
  address: string;
  taxCode: string;
  debt: string;
  note: string;
  isActive: boolean;
}

interface EditState {
  name: string;
  phone: string;
  address: string;
  taxCode: string;
  debt: string;
  note: string;
}

const emptyEdit: EditState = { name: "", phone: "", address: "", taxCode: "", debt: "", note: "" };

function fmtDebt(d: string) {
  const n = parseFloat(d);
  if (isNaN(n) || n === 0) return "—";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

export function SuppliersPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // roleAllows cho viewer đi qua mọi danh sách không-admin-only → phải loại
  // viewer trước, nếu không nút ghi vẫn hiện rồi BE mới chặn 403.
  const canMutate =
    !!user &&
    user.role !== "viewer" &&
    roleAllows(user.role as UserRole, ["admin", "manager", "warehouse"]);
  const canEditDebt = user?.role === "admin" || user?.role === "manager";

  const [editId, setEditId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>(emptyEdit);
  const [showNew, setShowNew] = useState(false);
  const [newState, setNewState] = useState<EditState>(emptyEdit);
  const [error, setError] = useState("");
  const [ledgerId, setLedgerId] = useState<string | null>(null);

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () =>
      apiClient.get<{ data: Supplier[] }>("/v1/suppliers").then((r) => r.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) =>
      apiClient.patch(`/v1/suppliers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setEditId(null);
      setError("");
    },
    onError: (e) => setError(translateApiError(e)),
  });

  const createMutation = useMutation({
    mutationFn: (body: object) => apiClient.post("/v1/suppliers", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setShowNew(false);
      setNewState(emptyEdit);
      setError("");
    },
    onError: (e) => setError(translateApiError(e)),
  });

  function startEdit(s: Supplier) {
    setEditId(s.id);
    setEditState({
      name: s.name,
      phone: s.phone,
      address: s.address,
      taxCode: s.taxCode,
      debt: s.debt,
      note: s.note,
    });
  }

  function saveEdit(id: string) {
    // Không đủ quyền sửa công nợ thì không gửi trường debt — gửi lên sẽ bị 403
    // dù người dùng chỉ đổi tên/SĐT.
    const { debt, ...rest } = editState;
    updateMutation.mutate({ id, body: canEditDebt ? { ...rest, debt } : rest });
  }

  function toggleActive(s: Supplier) {
    updateMutation.mutate({ id: s.id, body: { isActive: !s.isActive } });
  }

  function field(key: keyof EditState, state: EditState, setter: (s: EditState) => void, placeholder: string) {
    return (
      <Input
        className="h-7 text-sm"
        placeholder={placeholder}
        value={state[key]}
        onChange={(e) => setter({ ...state, [key]: e.target.value })}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Nhà cung cấp</h1>
        {canMutate && (
          <Button size="sm" className="gap-1.5" onClick={() => setShowNew((v) => !v)}>
            <Plus size={16} /> Thêm NCC
          </Button>
        )}
      </div>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      {showNew && canMutate && (
        <div className="flex flex-wrap gap-2 rounded-md border p-3 bg-muted/20">
          {field("name", newState, setNewState, "Tên công ty *")}
          {field("phone", newState, setNewState, "Điện thoại")}
          {field("address", newState, setNewState, "Địa chỉ")}
          {field("taxCode", newState, setNewState, "Mã số thuế")}
          {canEditDebt && field("debt", newState, setNewState, "Công nợ (₫)")}
          {field("note", newState, setNewState, "Ghi chú")}
          <Button
            size="sm"
            disabled={!newState.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(newState)}
          >
            Lưu
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
            <X size={16} />
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Tên công ty</Th>
              <Th>Điện thoại</Th>
              <Th>Địa chỉ</Th>
              <Th>Mã số thuế</Th>
              <Th className="text-right">Công nợ</Th>
              <Th>Ghi chú</Th>
              <Th>Trạng thái</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {suppliers.length === 0 && (
              <Tr>
                <Td colSpan={8} className="text-center text-muted-foreground">
                  Chưa có nhà cung cấp
                </Td>
              </Tr>
            )}
            {suppliers.map((s) => (
              <Tr key={s.id}>
                {editId === s.id ? (
                  <>
                    <Td>{field("name", editState, setEditState, "Tên *")}</Td>
                    <Td>{field("phone", editState, setEditState, "Điện thoại")}</Td>
                    <Td>{field("address", editState, setEditState, "Địa chỉ")}</Td>
                    <Td>{field("taxCode", editState, setEditState, "MST")}</Td>
                    <Td className="text-right tabular-nums">
                      {canEditDebt ? (
                        field("debt", editState, setEditState, "Công nợ")
                      ) : (
                        <span title="Chỉ quản lý sửa được công nợ">{fmtDebt(s.debt)}</span>
                      )}
                    </Td>
                    <Td>{field("note", editState, setEditState, "Ghi chú")}</Td>
                    <Td>—</Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(s.id)}>
                          <Check size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditId(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    </Td>
                  </>
                ) : (
                  <>
                    <Td className="font-medium">{s.name}</Td>
                    <Td>{s.phone || "—"}</Td>
                    <Td>{s.address || "—"}</Td>
                    <Td className="font-mono text-xs">{s.taxCode || "—"}</Td>
                    <Td className="text-right tabular-nums">{fmtDebt(s.debt)}</Td>
                    <Td className="text-muted-foreground text-xs">{s.note || "—"}</Td>
                    <Td>
                      <button
                        onClick={() => canMutate && toggleActive(s)}
                        disabled={!canMutate}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          canMutate ? "cursor-pointer" : "cursor-default"
                        } ${s.isActive ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}
                      >
                        {s.isActive ? "Hoạt động" : "Ẩn"}
                      </button>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="Sổ công nợ: nhập hàng ngày nào, trả tiền ngày nào"
                          onClick={() => setLedgerId(s.id)}
                        >
                          <BookOpen size={14} />
                        </Button>
                        {canMutate && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(s)}>
                            <Pencil size={14} />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {ledgerId && (
        <SupplierLedgerModal supplierId={ledgerId} onClose={() => setLedgerId(null)} />
      )}
    </div>
  );
}
